import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Layers, AlertCircle, CalendarDays, Users } from "lucide-react";
import { useLinearProjects, type LinearProject } from "@/hooks/useLinear";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatTile } from "@/components/shared/StatTile";
import { EmptyState } from "@/components/shared/EmptyState";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LINEAR_STATE_META, statusMeta } from "@/lib/statusTone";

function ProjectCard({ project }: { project: LinearProject }) {
  const state = project.status?.type?.toLowerCase() ?? "backlog";
  const stateLabel = project.status?.name ?? state;
  const stateTone = statusMeta(LINEAR_STATE_META, state).tone;
  const members = project.members?.nodes ?? [];
  const teams = project.teams?.nodes ?? [];

  return (
    <Link to={`/admin/linear/projects/${project.id}`} className="block">
      <Card className="cursor-pointer transition-all hover:border-primary/30 hover:shadow-md">
        <CardContent className="space-y-3 p-5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold leading-snug">{project.name}</h3>
            <StatusBadge shape="tag" tone={stateTone}>
              {stateLabel}
            </StatusBadge>
          </div>

          {project.description && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{project.description}</p>
          )}

          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Progresso</span>
              <span>{Math.round((project.progress ?? 0) * 100)}%</span>
            </div>
            <Progress value={(project.progress ?? 0) * 100} className="h-1.5" />
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            {project.targetDate && (
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {format(new Date(project.targetDate), "dd/MM/yyyy", { locale: ptBR })}
              </span>
            )}
            {members.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {members
                  .slice(0, 2)
                  .map((m) => m.name)
                  .join(", ")}
                {members.length > 2 && ` +${members.length - 2}`}
              </span>
            )}
            {teams.length > 0 && (
              <Badge variant="secondary" className="text-xs font-normal">
                {teams[0].name}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function LinearOverview() {
  const { data, isLoading, error } = useLinearProjects();
  const projects = data?.projects?.nodes ?? [];

  const total = projects.length;
  const inProgress = projects.filter((p) => p.status?.type === "started").length;
  const completed = projects.filter((p) => p.status?.type === "completed").length;
  const overdue = projects.filter((p) => {
    if (!p.targetDate) return false;
    return new Date(p.targetDate) < new Date() && p.status?.type !== "completed";
  }).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projetos — Linear"
        description="Visão geral dos projetos do seu workspace."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Total"
          value={total}
          icon={<Layers className="h-4 w-4" />}
          tone="primary"
        />
        <StatTile
          label="Em andamento"
          value={inProgress}
          icon={<Layers className="h-4 w-4" />}
          tone="warn"
        />
        <StatTile
          label="Concluídos"
          value={completed}
          icon={<Layers className="h-4 w-4" />}
          tone="ok"
        />
        <StatTile
          label="Atrasados"
          value={overdue}
          icon={<AlertCircle className="h-4 w-4" />}
          tone={overdue > 0 ? "bad" : "default"}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-start gap-3 p-6">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-medium">Não foi possível conectar ao Linear</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Configure a variável de ambiente{" "}
                <code className="rounded bg-muted px-1 font-mono">LINEAR_API_KEY</code> na edge
                function:
              </p>
              <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
                supabase secrets set LINEAR_API_KEY=lin_api_xxxxx
              </pre>
              <p className="mt-1 text-xs text-muted-foreground">
                Gere sua chave em: Linear → Settings → API → Personal API Keys
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && projects.length === 0 && (
        <EmptyState
          icon={<Layers className="h-5 w-5" />}
          title="Nenhum projeto encontrado no workspace."
          className="py-16"
        />
      )}

      {projects.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}
